import { parse, format, differenceInDays, addDays } from 'date-fns';
import { zhCN, ja, enUS, enAU } from 'date-fns/locale'
import { getLocale } from './pathHelper';

const parseDate = (dateString: Date|string) => {
  if (!dateString) { return null; }
  if (typeof(dateString) !== 'string') { return dateString; }
  return parse(dateString, 'yyyy-MM-dd', new Date());
}

const formatDate = (date: Date|string) => {
  if (!date) { return null; }
  if (typeof(date) === 'string') { return date; }
  return format(date, 'yyyy-MM-dd');
}

const dateFnsLocale = (): Locale|null => {
  // zhTW はまだサポートされていない
  const localeList = {
    zhCN,
    zhTW: zhCN,
    zhHK: zhCN,
    ja: ja,
    en: enUS,
    au: enAU,
  };
  const key = getLocale().replace('-', '');
  return localeList[key] || null;
}

const createDateList = (fromDate: Date|string, toDate: Date|string): Date[] => {
  const dateList: Date[] = [];
  if (fromDate == null || toDate == null) {
    return dateList;
  }
  const start = typeof(fromDate) === 'string' ? parseDate(fromDate) : fromDate;
  const end = typeof(toDate) === 'string' ? parseDate(toDate) : toDate;
  const selectedDuration: number = differenceInDays(end, start) + 1;
  for (let i = 0; i < selectedDuration; i++) {
    dateList.push(addDays(start, i));
  }
  return dateList;
}

/**
 * Format date with day of week according to locale
 * @param date Date or string to format
 * @returns formatted date string with day of week
 *
 * 参考: https://docs.google.com/spreadsheets/d/1xojJMdnRaEcf19o347rXhsLfZ6UjE1N59EUM01M6hrk/edit?gid=102504160#gid=102504160
 *
 * 例:
 * ja: 2025/03/03(月)
 * tw: 2025/03/03(週一)
 * hk: 03/03/2025(星期一)
 * cn: 2025/03/03(周一)
 * en: March 3, 2025 (Mon)
 * au: March 3, 2025 (Mon)
 */
const formatDateWithDayOfWeek = (date: Date|string): string => {
  if (!date) { return ''; }

  const parsedDate = typeof(date) === 'string' ? parseDate(date) : date;
  if (!parsedDate) { return ''; }

  const locale = getLocale();
  const dateFnsLoc = dateFnsLocale();
  
  const chineseWeekdays = ['日', '一', '二', '三', '四', '五', '六'];

  switch (locale) {
    case 'ja':
      return format(parsedDate, 'yyyy/MM/dd') + '(' + format(parsedDate, 'E', { locale: ja }) + ')';
    case 'zh-TW': {
      const dayIndex = parsedDate.getDay();
      return format(parsedDate, 'yyyy/MM/dd') + '(週' + chineseWeekdays[dayIndex] + ')';
    }
    case 'zh-HK': {
      const dayIndex = parsedDate.getDay();
      return format(parsedDate, 'dd/MM/yyyy') + '(星期' + chineseWeekdays[dayIndex] + ')';
    }
    case 'zh-CN': {
      const dayIndex = parsedDate.getDay();
      return format(parsedDate, 'yyyy/MM/dd') + '(周' + chineseWeekdays[dayIndex] + ')';
    }
    case 'en':
    case 'au':
    default:
      return format(parsedDate, 'MMMM d, yyyy', { locale: dateFnsLoc }) + ' (' + format(parsedDate, 'EEE', { locale: dateFnsLoc }) + ')';
  }
};

export { parseDate, formatDate, dateFnsLocale, createDateList, formatDateWithDayOfWeek }
